/**
 * AuthPanel — allowed connection methods (toggle + star recommended), OAuth
 * shape/scopes, API-key header field, and the vault note.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../../theme';
import { Star } from '../../../icons';
import { Toggle } from '../../../components';
import { useBridge } from '../../../store/bridgeStore';
import { allInts } from '../../../domain/catalog';
import { enabledMethods, recommendedMethod, METHOD_META } from '../../../domain/auth';
import type { AuthMethod } from '../../../domain/types';

const SCOPES = ['read:appointments', 'write:appointments', 'read:contacts'];

export default function AuthPanel({ adapterId, vendorName }: { adapterId: string; vendorName: string }) {
  const { state, actions } = useBridge();
  const ints = allInts(state.adapters, state.metaOverrides);
  const int = ints.find(i => i.id === adapterId);
  const cfg = state.authCfg[adapterId] || {};
  const enabled = enabledMethods(cfg, int);
  const rec = recommendedMethod(cfg, int);
  const oauthOn = enabled.includes('oauth');
  const apiKeyOn = enabled.includes('apikey');
  const headerName = cfg.headerName != null ? cfg.headerName : 'X-API-Key';

  const methods: AuthMethod[] = ['oauth', 'apikey', 'bearer'];

  return (
    <View style={{ gap: 14 }}>
      <Text style={styles.intro}>
        Choose which auth methods tenants may use to connect their own {vendorName} account. You're configuring the infrastructure — tenants supply their own credentials at connect time.
      </Text>

      <View>
        <Text style={styles.heading}>Allowed connection methods</Text>
        <View style={{ gap: 8 }}>
          {methods.map(m => {
            const on = enabled.includes(m);
            const isRec = on && rec === m;
            const [label, note] = METHOD_META[m];
            return (
              <View key={m} style={[styles.method, { borderColor: on ? colors.accentLine : colors.line } as any]}>
                <View style={{ flex: 1 }}>
                  <View style={styles.methodTop}>
                    <Text style={styles.methodLabel}>{label}</Text>
                    {isRec && (
                      <View style={styles.recBadge}><Text style={styles.recBadgeText}>recommended</Text></View>
                    )}
                  </View>
                  <Text style={styles.methodNote}>{note}</Text>
                </View>
                <Pressable
                  onPress={() => on && actions.setRecommendedMethod(adapterId, m)}
                  title="Mark recommended"
                  style={{ padding: 2, cursor: on ? 'pointer' : 'default' } as any}
                >
                  <Star size={15} color={isRec ? colors.warn : on ? colors.ink4 : colors.lineStrong} fill={isRec ? colors.warn : 'none'} />
                </Pressable>
                <Toggle on={on} onToggle={() => actions.toggleAuthMethod(adapterId, m)} />
              </View>
            );
          })}
        </View>
      </View>

      {oauthOn && (
        <>
          <View>
            <Text style={styles.fieldLabel}>OAuth shape</Text>
            <View style={styles.box}><Text style={styles.boxMono}>Authorization Code + PKCE</Text></View>
          </View>
          <View>
            <Text style={styles.fieldLabel}>Scopes requested</Text>
            <View style={styles.scopes}>
              {SCOPES.map(s => <View key={s} style={styles.scope}><Text style={styles.scopeText}>{s}</Text></View>)}
            </View>
          </View>
        </>
      )}

      {apiKeyOn && (
        <View>
          <Text style={styles.fieldLabel}>API-key header name</Text>
          <TextInput value={headerName} onChangeText={v => actions.authSetHeader(adapterId, v)} style={styles.input} />
          <Text style={styles.hint}>The header SK Bridge will send the tenant's key in.</Text>
        </View>
      )}

      <View style={styles.infoBox}>
        <Text style={styles.infoText}>
          SK Bridge never stores a platform credential here. Each tenant authorizes with their own account at connect time — their secrets live in the vault by reference.
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  intro: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, lineHeight: 19.5 },
  heading: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.ink3, fontWeight: fontWeight.semibold, marginBottom: 8 },
  method: { flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 11, paddingHorizontal: 12, borderWidth: 1, borderRadius: radii.r2, backgroundColor: colors.paper2 },
  methodTop: { flexDirection: 'row', alignItems: 'center', gap: 7 },
  methodLabel: { fontFamily: fontFamily.sans, fontWeight: fontWeight.medium, fontSize: fontSize.md, color: colors.ink },
  recBadge: { paddingHorizontal: 5, borderRadius: 3, borderWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.accentSoft },
  recBadgeText: { fontFamily: fontFamily.sans, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: fontWeight.semibold, color: colors.accentInk },
  methodNote: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 1 },
  fieldLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, marginBottom: 4 },
  box: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, backgroundColor: colors.paper2, paddingVertical: 9, paddingHorizontal: 11 },
  boxMono: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink },
  scopes: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 2 },
  scope: { paddingVertical: 3, paddingHorizontal: 9, borderRadius: radii.full, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2 },
  scopeText: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink2 },
  input: { width: '100%', height: 36, paddingHorizontal: 11, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: radii.r2, fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink as any, outlineStyle: 'none' } as any,
  hint: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 5 },
  infoBox: { padding: 10, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.infoLine, backgroundColor: colors.infoSoft, borderRadius: radii.r2 },
  infoText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.info, lineHeight: 18 },
});
